/**
 * Centre de Contrôle IA - Phase 5
 * Dashboard intelligent pour surveiller la production en temps réel
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle, TrendingUp, RefreshCw, Activity } from 'lucide-react';
import { formatNacelleDisplay } from '@/lib/formatNacelle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CentreControleIA() {
  const [analyse, setAnalyse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [joursAnalyse, setJoursAnalyse] = useState(30);

  const chargerAnalyse = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/ia/centre-controle/analyse-globale?jours_futur=${joursAnalyse}`);
      setAnalyse(res.data);
    } catch (err) {
      console.error('Erreur chargement analyse:', err);
      alert('Erreur lors du chargement de l\'analyse');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    chargerAnalyse();
  }, []);

  if (!analyse && loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-lg text-slate-600">Analyse de la production en cours...</p>
        </div>
      </div>
    );
  }

  if (!analyse) return null;

  const { statistiques, conflits, incoherences, suggestions_ia, sante_globale } = analyse;

  const getSanteColor = (sante) => {
    if (sante === 'bonne') return 'bg-green-100 text-green-800 border-green-300';
    if (sante === 'moyenne') return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  const getSanteIcon = (sante) => {
    if (sante === 'bonne') return <CheckCircle className="w-6 h-6 text-green-600" />;
    if (sante === 'moyenne') return <Activity className="w-6 h-6 text-yellow-600" />;
    return <AlertTriangle className="w-6 h-6 text-red-600" />;
  };

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Activity className="w-8 h-8 text-blue-600" />
            Centre de Contrôle IA
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Surveillance et analyse intelligente de la production
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={joursAnalyse}
            onChange={(e) => setJoursAnalyse(parseInt(e.target.value))}
            className="border rounded-md px-3 py-2 text-sm"
          >
            <option value={7}>7 jours</option>
            <option value={14}>14 jours</option>
            <option value={30}>30 jours</option>
            <option value={60}>60 jours</option>
          </select>
          <Button onClick={chargerAnalyse} disabled={loading} size="sm">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Santé Globale */}
      <Card className={`border-2 ${getSanteColor(sante_globale)}`}>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            {getSanteIcon(sante_globale)}
            <div className="flex-1">
              <h2 className="text-2xl font-bold">
                Santé de la production: {sante_globale === 'bonne' ? 'BONNE' : sante_globale === 'moyenne' ? 'MOYENNE' : 'CRITIQUE'}
              </h2>
              <p className="text-sm mt-1">
                {sante_globale === 'critique' && '⚠️ Attention: Plusieurs conflits détectés nécessitant une action immédiate'}
                {sante_globale === 'moyenne' && 'ℹ️ Quelques incohérences détectées, surveillance recommandée'}
                {sante_globale === 'bonne' && '✅ Tout fonctionne correctement'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistiques */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-slate-600">Bons de Travail</div>
            <div className="text-3xl font-bold text-blue-600">{statistiques.nb_bons_travail}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-slate-600">Routes de Lavage</div>
            <div className="text-3xl font-bold text-cyan-600">{statistiques.nb_routes_lavage}</div>
          </CardContent>
        </Card>
        <Card className="border-red-200">
          <CardContent className="p-4">
            <div className="text-sm text-slate-600">Conflits Véhicules</div>
            <div className="text-3xl font-bold text-red-600">{statistiques.nb_conflits_vehicules}</div>
          </CardContent>
        </Card>
        <Card className="border-orange-200">
          <CardContent className="p-4">
            <div className="text-sm text-slate-600">Conflits Nacelles</div>
            <div className="text-3xl font-bold text-orange-600">{statistiques.nb_conflits_nacelles}</div>
          </CardContent>
        </Card>
      </div>

      {/* Conflits Véhicules */}
      {conflits.vehicules.length > 0 && (
        <Card className="border-red-300">
          <CardHeader>
            <CardTitle className="text-red-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Conflits Véhicules ({conflits.vehicules.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {conflits.vehicules.map((conflit, idx) => (
                <div key={conflit.id || conflit._id || idx} className="border border-red-200 rounded-md p-3 bg-red-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-semibold text-red-900">
                        📅 {conflit.date} — {conflit.vehicule_nom}
                      </div>
                      <div className="text-sm text-red-700 mt-1">
                        {conflit.nb_occupations} occupation(s) simultanée(s):
                      </div>
                      <ul className="text-xs text-red-600 mt-2 space-y-1">
                        {conflit.occupations.map((occ, oidx) => (
                          <li key={oidx} className="ml-4">
                            • {occ.type}: {occ.client_nom || occ.chef_nom}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <span className="px-2 py-1 bg-red-200 text-red-800 text-xs font-semibold rounded">
                      {conflit.severite.toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Conflits Nacelles */}
      {conflits.nacelles.length > 0 && (
        <Card className="border-orange-300">
          <CardHeader>
            <CardTitle className="text-orange-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Conflits Nacelles ({conflits.nacelles.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {conflits.nacelles.map((conflit, idx) => (
                <div key={conflit.id || conflit._id || idx} className="border border-orange-200 rounded-md p-3 bg-orange-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-semibold text-orange-900">
                        📅 {conflit.date} — {formatNacelleDisplay({numero_unite: conflit.nacelle_numero_unite, nom: conflit.nacelle_nom})}
                      </div>
                      <div className="text-sm text-orange-700 mt-1">
                        {conflit.nb_occupations} utilisation(s) simultanée(s):
                      </div>
                      <ul className="text-xs text-orange-600 mt-2 space-y-1">
                        {conflit.occupations.map((occ, oidx) => (
                          <li key={oidx} className="ml-4">
                            • {occ.type}: {occ.client_nom || ''} (Chef: {occ.chef_nom})
                          </li>
                        ))}
                      </ul>
                    </div>
                    <span className="px-2 py-1 bg-orange-200 text-orange-800 text-xs font-semibold rounded">
                      {conflit.severite.toUpperCase()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Incohérences */}
      {incoherences.length > 0 && (
        <Card className="border-yellow-300">
          <CardHeader>
            <CardTitle className="text-yellow-800 flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Incohérences Détectées ({incoherences.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {incoherences.map((inc, idx) => (
                <div key={inc.id || inc._id || idx} className="border border-yellow-200 rounded-md p-2 bg-yellow-50 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-yellow-900">
                      {inc.type}: {inc.client_nom || inc.message}
                    </span>
                    <span className="text-xs px-2 py-0.5 bg-yellow-200 text-yellow-800 rounded">
                      {inc.severite}
                    </span>
                  </div>
                  <p className="text-xs text-yellow-700 mt-1">{inc.message}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Suggestions IA */}
      <Card className="border-blue-300 bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardHeader>
          <CardTitle className="text-blue-900 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            💡 Suggestions d'Optimisation (IA GPT-4)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {suggestions_ia.length === 0 ? (
            <p className="text-sm text-blue-600 italic">Aucune suggestion pour le moment</p>
          ) : (
            <div className="space-y-3">
              {suggestions_ia.map((sugg, idx) => (
                <div
                  key={sugg.id || sugg._id || idx}
                  className={`border rounded-md p-3 ${
                    sugg.priorite === 'haute'
                      ? 'border-red-300 bg-red-50'
                      : sugg.priorite === 'moyenne'
                      ? 'border-yellow-300 bg-yellow-50'
                      : sugg.priorite === 'info'
                      ? 'border-blue-300 bg-blue-50'
                      : 'border-green-300 bg-green-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-sm">{sugg.titre}</h3>
                      <p className="text-xs mt-1 text-slate-700">{sugg.description}</p>
                    </div>
                    <span
                      className={`text-xs px-2 py-1 rounded font-semibold ${
                        sugg.priorite === 'haute'
                          ? 'bg-red-200 text-red-800'
                          : sugg.priorite === 'moyenne'
                          ? 'bg-yellow-200 text-yellow-800'
                          : 'bg-blue-200 text-blue-800'
                      }`}
                    >
                      {sugg.priorite}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center text-xs text-slate-500">
        Dernière analyse: {new Date(analyse.date_analyse).toLocaleString('fr-CA')}
      </div>
    </div>
  );
}

export default CentreControleIA;
